import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, NavLink } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import SubmitButton from '../components/SubmitButton';
import FormVarH from '../components/FormVarH';
import TechTimeEntryCp from './TechTimeEntryCp';
import { UtilConvertTimeStampToDate } from '../utils/UtilConvertTimeStampToDate';
import { UtilConvertDateTodayDate } from '../utils/UtilConvertDateTodayDate';

const TimesheetLogCp = ({ date, payperiod, prefillLogId }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const api = `${apiUrl}/tech_crud.php`;
  const navigate = useNavigate();
  const [timesheetLog, setTimesheetLog] = useState({
    id: '',
    tech_id: '',
    date: '',
    time_type_input: 'log',
    numberid: '',
    address: '',
    contractor: '',
    job_status: '',
    over_hrs: '',
    reg_hrs: '',
    time_in: '',
    time_out: '',
    notes: '',
    pay_period: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobSelect, setJobSelect] = useState('');
  const [bodyData, setBodyData] = useState({
    status: 'submit_log',
    current_date: '',
    data: {}
  });
  const [verifyResult, setVerifyResult] = useState(null);

  // NEW for updating existing log ===========
  const [isEditing, setIsEditing] = useState(false);
  // NEW for updating existing log ===========

  const getPayperiodRange = (payperiod) => {
    // payperiod should be in 'YYYY-MM-DD' format and is the FRIDAY payday
    const payDate = new Date(payperiod);
  
    // Find the previous Sunday before paydate
    const end = new Date(payDate);
    // Get difference in days to previous Sunday (0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday)
    const diffToSunday = (end.getDay() + 6) % 6 + 1;
    end.setDate(payDate.getDate() - diffToSunday);
  
    // Start = 13 days before end (so 14 days total)
    const start = new Date(end);
    start.setDate(end.getDate() - 13);
  
    // Format as YYYY-MM-DD
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  
    return {
      start: fmt(start),
      end: fmt(end),
    };
  };
  
  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
  
    setTimesheetLog((prev) => ({
      ...prev,
      pay_period: payperiod,
      date: date,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'numberid' && value) {
      verifyJobAssignment(value, timesheetLog.tech_id);

    }
  };

  const handleWeek = (job) => {
    setTimesheetLog((prev) => ({
      ...prev,
      address: job.job_site,
      contractor: job.customer_name,
      date: date,
      numberid: job.job_number,
      time_type_input: 'Log',
      id: 'New',
      over_hrs: '',
      notes: '',
      pay_period: payperiod
    }));
    setJobSelect(job);
    if (job) {
      setJobs([]);
      setSearchTerm('');
    }
  };

  const handleSearchClick = async () => {
    setLoading(true);

    const bodyData = {
      status: 'search_job',
      data: { search: searchTerm }
    };

    try {
      const response = await axios.post(`${apiUrl}/tech_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data && Array.isArray(response.data)) {
        setJobs(response.data);
        setJobSelect('');
      } else {
        setJobs([]);
        toast.error('not found ' + searchTerm, {
          position: 'top-center'
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", {
          position: "top-center"
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: "top-center"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (value === '') {
      setJobs(jobs);
    } else {
      const filtered = jobs.filter(jobs =>
        (jobs.customer_name && jobs.customer_name.toLowerCase().includes(value.toLowerCase())) ||
        (jobs.job_number && jobs.job_number.toLowerCase().includes(value.toLowerCase())) ||
        (jobs.job_site && jobs.job_site.toLowerCase().includes(value.toLowerCase()))
      );
      setJobs(filtered);
    }
  };

  const updateBodyData = useCallback(() => {
    setBodyData((prev) => ({
      ...prev,
      current_date: date,
      data: { ...timesheetLog }
    }));
  }, [date, timesheetLog]);

  // helpers (put near top of file)
  const toTimeInput = (s) => {
    if (!s) return '';
    // supports "8:05 PM", "08:05 pm", "20:05", "08:05"
    const ampm = s.trim().toLowerCase().match(/\b(am|pm)\b/);
    if (ampm) {
      const [hhmm] = s.trim().toLowerCase().split(/\s+/); // "8:05"
      let [h, m] = hhmm.split(':').map(Number);
      if (ampm[0] === 'pm' && h !== 12) h += 12;
      if (ampm[0] === 'am' && h === 12) h = 0;
      return `${String(h).padStart(2,'0')}:${String(m||0).padStart(2,'0')}`;
    }
    // already 24h? normalize to HH:MM
    const [h,m='00'] = s.split(':');
    return `${String(h).padStart(2,'0')}:${String(parseInt(m,10)||0).padStart(2,'0')}`;
  };

  const to12h = (hhmm) => {
    if (!hhmm) return '';
    let [h, m='00'] = hhmm.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${String(m||0).padStart(2,'0')} ${ampm}`;
  };

  useEffect(() => {
    updateBodyData();
  }, [timesheetLog, updateBodyData]);  

   useEffect(() => {
    const fetchLogById = async (id) => {
      try {
        const res = await axios.post(`${apiUrl}/tech_crud.php`, {
          status: 'get_log_by_id',
          data: { log_id: Number(id) }
        }, { headers: { 'Content-Type': 'application/json' } });
        if (res.data && res.data.id) {
          const log = res.data;
          setTimesheetLog(prev => ({
            ...prev,
            id: log.id,
            tech_id: log.tech_id ?? prev.tech_id,
            date: log.date || date,
            time_type_input: 'log',
            numberid: log.numberid || '',
            address: log.address || '',
            contractor: log.contractor || '',
            job_status: log.job_status || '',
            over_hrs: log.over_hrs || '',
            reg_hrs: log.reg_hrs || '',
            time_in: toTimeInput(log.time_in),
            time_out: toTimeInput(log.time_out),
            notes: log.notes || '',
            pay_period: log.pay_period || payperiod,
          }));

          setVerifyResult({ success: true }); // existing item, allow edit
          setIsEditing(true);
        } else {
          toast.error('Log not found', { position: 'top-center' });
        }
      } catch (e) {
        toast.error('Failed to load log', { position: 'top-center' });
      }
    };

    if (prefillLogId) fetchLogById(prefillLogId);
  }, [prefillLogId, apiUrl, date, payperiod]);

  const handleFormSubmit = async (event) => {
    event.preventDefault();
  
    // Block if verification is not done or not successful
    if (!verifyResult || !verifyResult.success) {
      toast.error("You are not assigned to this call for this pay period. Cannot submit.", { position: "top-center" });
      return;
    }

    const payload = {
      status: isEditing ? 'update_log' : 'submit_log',
      current_date: date,
      data: { ...timesheetLog }
    };

    try {
      const response = await axios.post(api, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        toast.info('Saved!', {
          position: 'top-center'
        });
        setTimeout(() => {
          navigate('/t/twkt');
        }, 1000); // Delay navigation for 1 second
      } else {
        toast.error(response.data, {
          position: 'top-center'
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", {
          position: "top-center"
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: "top-center"
        });
      }
    }
  };

  const verifyJobAssignment = async (numberid) => {
    if (!payperiod ) return false;
  
    const { start, end } = getPayperiodRange(payperiod);
  
    try {
      const res = await axios.post(api, {
        status: "verify_tech_job_assignment",
        data: {
          call_id: numberid,
          week_start: start,
          week_end: end,
        }        
      }, { headers: { "Content-Type": "application/json" } });
      setVerifyResult(res.data);
      if (res.data.success) {
        if (res.data.job_site) {
          setTimesheetLog(prev => ({
            ...prev,
            address: res.data.job_site
          }));
        }
        toast.success("Tech is assigned to this job for this pay period!", { position: 'top-center' });
        return true;
      } else {
        setTimesheetLog(prev => ({
          ...prev,
          address:""
        }));
        // toast.info(res.data.message || "Not assigned!", { position: 'top-center' });
        return false;
      }
    } catch (err) {
      toast.error("Verification failed.", { position: 'top-center' });
      return false;
    }
  };

  return (
    <>
      <br />
      <HeadingCp label={"Service log entry"} />
      <>
        <form
          onSubmit={handleFormSubmit}
          className='px-6 bg-white border rounded-lg py-8 mt-4'>
          <div className="space-y-12">
            <div className="border-b border-gray-900/10 pb-12">
            <h6 className="text-base font-semibold leading-7 text-gray-600">
              <div className="sm:col-span-3">
                <label
                  htmlFor="numberid"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Log number <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  <input
                    type="number"                                // ✅ numeric keyboard on mobile
                    name="numberid"
                    id="numberid"
                    value={timesheetLog.numberid || ''}
                    onChange={(e) => {
                      // ✅ strip non-digit characters before updating
                      const numericValue = e.target.value.replace(/\D/g, '');
                      handleChange({ target: { name: 'numberid', value: numericValue } });
                    }}
                    inputMode="numeric"                          // ✅ ensures numeric keypad on iOS/Android
                    pattern="[0-9]*"                             // ✅ HTML5 validation for digits only
                    autoComplete="off"
                    required
                    className="text-slate-500 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </h6>
              <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-3 ">
                <label htmlFor='address' className="block text-sm font-medium leading-6 text-gray-900">
                  Job Site <span className='text-red-500'>*</span>
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name='address'
                    id='address'
                    value={timesheetLog.address || ''}
                    onChange={handleChange}
                    autoComplete='address'
                    required={true}
                    disabled={true}
                    className="text-slate-500 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>                  
              {verifyResult && verifyResult.success && (
                <TechTimeEntryCp handleChange={handleChange} timesheetLog={timesheetLog} />
              )}
              </div>
            </div>
          </div>

          {isEditing && (
            <div className="mb-2 inline-flex items-center rounded bg-amber-100 text-amber-700 px-2 py-1 text-xs">
              Editing existing log (#{timesheetLog.numberid})
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-x-6">
          {verifyResult && verifyResult.success && (
            <SubmitButton disabled={''}>Submit</SubmitButton>
          )}
          </div>
          <br/>
          <br/>
        </form>
        
      </>
    </>
  );
};

export default TimesheetLogCp;
